import useAdminStatus from "@/hooks/useAdminStatus";
import useCurrentUser from "@/hooks/useCurrentUser";
import useNotes from "@/src/notes/hooks/useNotes";
import { NoteType } from "@/src/notes/types/NoteType";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/solid";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { observer } from "mobx-react";
import { useState } from "react";

const NoteMenu = ({ note, onEditRequest = () => null }: NoteMenuProps) => {
  const { currentUser } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const { isUserAdmin } = useAdminStatus();

  const { deleteNote, disableNote, pinNote, unpinNote } = useNotes({
    elementId: note.elementId,
  });

  const handleDeleteClick = () => {
    deleteNote(note.uid);
    setIsOpen(false);
  };

  const handleEditClick = () => {
    onEditRequest();
    setIsOpen(false);
  };

  const handlePinClick = () => {
    if (note.isPinned) {
      unpinNote(note.uid);
    } else {
      pinNote(note.uid);
    }
    setIsOpen(false);
  };

  const belongsToCurrentUser = note.user?.uid === currentUser?.uid;

  return (
    <div className={clsx("relative")}>
      <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            className={clsx(
              "h-component-sm w-component-sm flex items-center justify-center rounded-full bg-zinc-50 hover:bg-zinc-950 hover:text-zinc-50",
              { "bg-zinc-950 text-zinc-50": isOpen }
            )}
          >
            <EllipsisHorizontalIcon className={clsx("size-5")} />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={clsx(
              "z-50 w-40 overflow-hidden rounded-md bg-zinc-950 font-body text-sm text-zinc-50"
            )}
          >
            <DropdownMenu.Arrow className={clsx("fill-zinc-800")} />
            {(belongsToCurrentUser || isUserAdmin) && (
              <DropdownItem onClick={handleEditClick}>
                <div className={clsx("flex items-center justify-between")}>
                  <div>Edit</div>
                  {isUserAdmin && <_AdminToken />}
                </div>
              </DropdownItem>
            )}
            {(belongsToCurrentUser || isUserAdmin) && (
              <DropdownItem onClick={handleDeleteClick}>
                <div className={clsx("flex items-center justify-between")}>
                  <div>Delete</div>
                  {isUserAdmin && <_AdminToken />}
                </div>
              </DropdownItem>
            )}
            {/* TODO: hide for now */}
            {isUserAdmin && false && (
              <DropdownItem
                onClick={() => {
                  disableNote(note.uid);
                  setIsOpen(false);
                }}
              >
                <div className={clsx("flex items-center justify-between")}>
                  <div>Disable</div>
                  {isUserAdmin && <_AdminToken />}
                </div>
              </DropdownItem>
            )}
            {isUserAdmin && !note.parentUid && (
              <DropdownItem onClick={handlePinClick}>
                <div className={clsx("flex items-center justify-between")}>
                  <div>{note.isPinned ? "Unpin" : "Pin"}</div>
                  <_AdminToken />
                </div>
              </DropdownItem>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
};

const _AdminToken = () => {
  return (
    <div
      className={clsx(
        "rounded-full bg-zinc-200 px-1.5 text-[11px] text-zinc-950"
      )}
    >
      A
    </div>
  );
};

interface NoteMenuProps {
  note: NoteType;
  onEditRequest?: () => void;
}

interface DropdownItemProps {
  children: React.ReactNode;
  onClick: () => void;
}

const DropdownItem = ({ children, onClick }: DropdownItemProps) => (
  <DropdownMenu.Item asChild>
    <button
      className={clsx(
        "w-full border-b border-b-zinc-800 px-4 py-2 text-left outline-none transition ease-out last:border-b-0 hover:border-b-zinc-800",
        {
          "hover:bg-zinc-800 focus:bg-zinc-800": true,
        }
      )}
      onClick={onClick}
    >
      {children}
    </button>
  </DropdownMenu.Item>
);

export default observer(NoteMenu);
